import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Text } from '@/components/ui/text';
import { useDeleteProjectService } from '@/services/projects';
import { Trash2 } from 'lucide-react';
import { type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface DeleteProjectDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  navigateOnDelete?: boolean;
}

export const DeleteProjectDialog: FC<DeleteProjectDialogProps> = ({
  projectId,
  open,
  onOpenChange,
  navigateOnDelete = false,
}) => {
  const navigate = useNavigate();
  const { deleteProject, isDeleting } = useDeleteProjectService();

  const handleConfirm = () => {
    deleteProject(projectId, {
      onSuccess: () => {
        onOpenChange(false);
        toast.success('Project deleted');
        if (navigateOnDelete) {
          navigate('..');
        }
      },
      onError: () => {
        toast.error('Failed to delete project');
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete project?</DialogTitle>
          <DialogDescription>
            <Text variant="body-sm" color="primary">
              Are you sure you want to delete this project? Chats and table
              views linked to this project will not be deleted, only unlinked.
            </Text>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            loading={isDeleting}
          >
            <Trash2 />
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
